import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { MapControlState, MapDisplayTypeEnum } from 'src/app/store/map-control/map-control.reducer';
import * as fromApp from '../../../store';
import { toggleDrawPolygonMode, toggleMapControlsBaseMap, toggleMapControlsEditMode, toggleMapControlsForest, toggleMapControlsKMLUpload, toggleMapControlsPublicLand, toggleMapControlsRoads, toggleMapControlsStands, toggleMapControlsStandSelector, toggleMapControlsSuggestedStandsSelector, toggleMapControlsWaterways, } from '../../../store/map-control/map-control.actions';
import { selectMapControlState } from '../../../store/map-control/map-control.selectors';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { environment } from "../../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { isNumber, keys, map, pickBy } from 'lodash';
import { MapService } from "../../../core/helper/map.service";

@Component({
  selector: 'fc-map-layers-selector',
  templateUrl: './map-layers-selector.component.html',
  styleUrls: ['./map-layers-selector.component.scss'],
})
export class MapLayersSelectorComponent implements OnInit {
  mapControls$: Observable<MapControlState>;

  @Input() mapService: MapService;
  @Input() standMap = false;
  @Input() isAdmin = false;

  currentMapStyle: MapDisplayTypeEnum = MapDisplayTypeEnum.SATELLITE;
  destroyRef = inject(DestroyRef);
  styleDefinitionSatellite;
  styleDefinitionContour;

  @Output() mapStyleLoading: EventEmitter<any> = new EventEmitter();

  constructor(
    readonly store: Store<fromApp.AppState>,
    readonly http: HttpClient,
  ) {
    this.mapControls$ = store.select(selectMapControlState);
  }

  ngOnInit(): void {
    let firstLoad = true;
    this.store
      .select(selectMapControlState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mapControls => {
        // Should we toggle base map?
        if (!firstLoad) {
          if (this.mapService.currentMap && mapControls.displayContour !== this.currentMapStyle) {
            this.toggleBaseMap(mapControls);
          } else if (this.mapService.currentMap.getLayer('waterways-data1')) { // using waterways-data1 as a proxy for the map being loaded
            this.mapService.setLayerVisibility(mapControls);
          }
        }
        firstLoad = false;
      });
    this.http
      .get(
        `https://api.mapbox.com/styles/v1/${environment.mapbox.contourStyleId}?access_token=${environment.mapbox.accessToken}`
      )
      .subscribe((result: any) => {
        // set the topographic font color and size
        const contourLabelIdx = result.layers.findIndex(l => l.id === 'contour-label');
        if (contourLabelIdx > -1) {
          result.layers[contourLabelIdx].paint['text-color'] = 'rgb(0, 0, 0)';
          result.layers[contourLabelIdx].layout['text-size'] = 32;
        }

        // set the topographic line color and thickness
        const contourLineIdx = result.layers.findIndex(l => l.id === 'contour-line');
        if (contourLineIdx > -1) {
          result.layers[contourLineIdx].paint['line-color'] = 'rgb(0, 0, 0)';
          result.layers[contourLineIdx].paint['line-width'] = [
            'interpolate',
            ['linear'],
            ['zoom'],
            13,
            ['match', ['get', 'index'], [1, 2], 0.5, 0.6],
            16,
            ['match', ['get', 'index'], [1, 2], 3, 3],
          ];
        }

        // make national parks our "park" color
        const nationalParkIdx = result.layers.findIndex(l => l.id === 'national-park');
        if (nationalParkIdx > -1) {
          result.layers[nationalParkIdx].paint['fill-color'] = 'rgba(179, 210, 162, .5)';
        }

        // make green spaces our "park" color
        const landuseIdx = result.layers.findIndex(l => l.id === 'landuse');
        if (landuseIdx > -1) {
          // find all the zoom levels and set the park color
          map(keys(pickBy(result.layers[landuseIdx].paint['fill-color'], isNumber)), Number).forEach((idx: number) => {
            result.layers[landuseIdx].paint['fill-color'][idx + 1][3][3] = 'rgba(179, 210, 162, .5)';
            result.layers[landuseIdx].paint['fill-color'][idx + 1][3][4] = 'rgba(179, 210, 162, .5)';
          });
        }

        this.styleDefinitionContour = result;
      });

    this.http
      .get(
        `https://api.mapbox.com/styles/v1/${environment.mapbox.satelliteStyleId}?access_token=${environment.mapbox.accessToken}`
      )
      .subscribe(result => {
        this.styleDefinitionSatellite = result;
      });
  }


  toggleMapControlsEvent(modeType: string) {
    switch (modeType) {
      case 'standSelector':
        this.store.dispatch(toggleMapControlsStandSelector());
        break;
      case 'editMode':
        this.store.dispatch(toggleMapControlsEditMode());
        break;
      case 'drawPolygonMode':
        this.store.dispatch(toggleDrawPolygonMode());
        break;
      case 'displayForest':
        this.store.dispatch(toggleMapControlsForest());
        break;
      case 'suggestedStandsSelector':
        this.store.dispatch(toggleMapControlsSuggestedStandsSelector());
        break;
      case 'displayWaterways':
        this.store.dispatch(toggleMapControlsWaterways());
        break;
      case 'displayStands':
        this.store.dispatch(toggleMapControlsStands());
        break;
      case 'displayRoads':
        this.store.dispatch(toggleMapControlsRoads());
        break;
      case 'switchMapMode':
        this.store.dispatch(toggleMapControlsBaseMap());
        break;
      case 'displayPublicLand':
        this.store.dispatch(toggleMapControlsPublicLand());
        break;
      case 'displayKMLUpload':
        this.store.dispatch(toggleMapControlsKMLUpload());
        break;
      default:
        throw new Error('invalid modeType');
    }
  }


  toggleBaseMap(mapControlState: MapControlState): void {

    this.mapStyleLoading.emit();
    this.currentMapStyle =
      this.currentMapStyle === MapDisplayTypeEnum.SATELLITE ? MapDisplayTypeEnum.CONTOUR : MapDisplayTypeEnum.SATELLITE;

    const currentStyle = this.mapService.currentMap.getStyle();
    const newStyle =
      this.currentMapStyle === MapDisplayTypeEnum.SATELLITE
        ? this.styleDefinitionSatellite
        : this.styleDefinitionContour;

    // ensure any sources from the current style are copied across to the new style
    newStyle.sources = { ...currentStyle.sources, ...newStyle.sources };

    // find the index of where to insert our layers to retain in the new style
    let labelIndex = newStyle.layers.findIndex(el => {
      return el.id === (this.standMap ? 'kml-import' : 'unavailable_area');
    });

    if (labelIndex === -1) {
      labelIndex = newStyle.layers.length;

      const appLayers = currentStyle.layers.filter(el => {
        // app layers are the layers to retain, and these are any layers which have a different source set
        return (
          el.source && el.source !== 'mapbox://mapbox.satellite' && el.source !== 'mapbox' && el.source !== 'composite'
        );
      });

      newStyle.layers = [
        ...newStyle.layers.slice(0, labelIndex),
        ...appLayers,
        ...newStyle.layers.slice(labelIndex, -1),
      ];
    }
    const parcelOutline = newStyle.layers.findIndex(l => l.id === (this.standMap ? 'parcel-data-outline2' : 'parcel-data-outline'));
    if (parcelOutline > -1) {
      newStyle.layers[parcelOutline].paint['line-color'] =
        this.currentMapStyle == MapDisplayTypeEnum.SATELLITE ? 'white' : 'black';
    }

    this.mapService.currentMap.setStyle(newStyle, { diff: false });
    this.mapService.reloadImages();
    this.mapService.currentMap.once('styledata', () => {
      this.mapService.setLayerVisibility(mapControlState);
    })
  }

}
